import React, { useEffect, useState } from "react";
import { UserPlus, Check, UserCheck, Clock, ChevronDown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const LABELS = [["friend", "Friend"], ["connect", "Connect"], ["family", "Family"]];

// Compact Follow + Connect control for any user, usable on posts & profiles.
export function ConnectButton({ userId, size = "sm" }) {
  const { user } = useApp();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);

  const load = async () => {
    if (!user || !userId || userId === user.user_id) return;
    try { const { data } = await api.get(`/circle/status/${userId}`); setSt(data); } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, user?.user_id]);

  if (!user || !userId || userId === user.user_id || !st) return null;

  const follow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/circle/follow", { user_id: userId }); setSt((s) => ({ ...s, following: data.following })); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const connect = async (label) => {
    setMenu(false); setBusy(true);
    try {
      const { data } = await api.post("/circle/connect", { user_id: userId, label });
      setSt((s) => ({ ...s, pending_out: data.status === "requested", connected: data.status === "accepted" }));
      toast.success(data.status === "accepted" ? "Added to your Kind Circle 🤝" : "Connect request sent 🙏");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cls = size === "xs" ? "h-7 px-2.5 text-[11px]" : "h-8 px-3 text-xs";

  return (
    <div className="flex items-center gap-1.5" data-testid={`connect-controls-${userId}`}>
      {st.connected ? (
        <span className={`${cls} rounded-full bg-brand/15 text-brand flex items-center gap-1`} data-testid={`connected-${userId}`}><UserCheck size={13} /> In your circle</span>
      ) : st.pending_out ? (
        <span className={`${cls} rounded-full border border-amber-400/40 text-amber-400 flex items-center gap-1`} data-testid={`pending-${userId}`}><Clock size={12} /> Requested</span>
      ) : (
        <div className="relative">
          <button data-testid={`connect-btn-${userId}`} disabled={busy} onClick={() => setMenu((m) => !m)}
            className={`${cls} rounded-full btn-primary flex items-center gap-1 disabled:opacity-60`}>
            <UserPlus size={13} /> {st.pending_in ? "Accept" : "Connect"} <ChevronDown size={11} />
          </button>
          {menu && (
            <div className="absolute right-0 mt-1 z-20 rud-card rounded-xl p-1 min-w-[130px] shadow-xl" data-testid={`connect-menu-${userId}`}>
              {LABELS.map(([id, label]) => (
                <button key={id} data-testid={`connect-as-${id}-${userId}`} onClick={() => connect(id)}
                  className="w-full text-left px-3 py-1.5 text-xs rounded-lg hover:bg-white/10 transition-colors">As {label}</button>
              ))}
            </div>
          )}
        </div>
      )}
      {!st.connected && (
        <button data-testid={`follow-btn-${userId}`} disabled={busy} onClick={follow}
          className={`${cls} rounded-full border flex items-center gap-1 transition-colors ${st.following ? "border-brand text-brand" : "border-rud text-sec hover:border-brand"}`}>
          {st.following ? <><Check size={12} /> Following</> : "Follow"}
        </button>
      )}
    </div>
  );
}

export default ConnectButton;
